/**
 * Hook for launching markets - handles permit signing, batch creation, and Convex recording.
 */
import { api } from "@breakout/backend/convex/_generated/api"
import {
	breakoutMarketFactoryAbi,
	calculateReserve,
	calculateSeededReserve,
	calculateTotalReserve,
	createAllMarketsWithPermit,
	getFactoryInfo,
	getPermitNonce,
	getTokenName,
	type MarketBatchConfig,
	oddsToQuantities,
	signPermit,
	validateOdds,
} from "@breakout/sdk"
import { BINARY_OUTCOME_LABELS } from "@breakout/shared/constants"
import { deadlineSec } from "@breakout/shared/utils/time"
import { createId } from "@paralleldrive/cuid2"
import { useQuery } from "@tanstack/react-query"
import { useAction } from "convex/react"
import { useCallback, useMemo, useState } from "react"
import type { Address, Hex } from "viem"
import { decodeEventLog, erc20Abi, keccak256, stringToBytes } from "viem"
import { useAccount, useWalletClient } from "wagmi"
import { E2E_MODE } from "../lib/env"
import { useAdminConfig } from "../lib/providers"
import { CUSTOM_QUESTION_CONFIG } from "../routes/launch/-config"
import type { KaitoEntry, LaunchFormState, SnapshotDetails } from "../routes/launch/-types"
import { toEpochSeconds, validateMarketTimes } from "../routes/launch/-utils"
import { useAdminAuth } from "./useAdminAuth"

interface UseLaunchWeekParams {
	formState: LaunchFormState
	snapshotDetails: SnapshotDetails | null
	entries: KaitoEntry[]
}

type LaunchResult =
	| { success: true; txHash: string; marketCount: number }
	| { success: false; error: string }

export function useLaunchWeek({ formState, snapshotDetails, entries }: UseLaunchWeekParams) {
	const { chainConfig, publicClient } = useAdminConfig()
	const { address } = useAccount()
	const { data: walletClient } = useWalletClient()
	const { password: adminPassword } = useAdminAuth()
	const [isLaunching, setIsLaunching] = useState(false)
	const [launchProgress, setLaunchProgress] = useState<string | null>(null)

	// Convex actions
	const uploadSnapshotToIpfsAction = useAction(api.domains.admin.actions.uploadSnapshotToIpfs)
	const upsertMarketsBatchAction = useAction(api.domains.admin.actions.upsertMarketsBatch)

	// Factory info query
	const { data: factoryInfo } = useQuery({
		queryKey: ["factory-info", chainConfig.factory],
		queryFn: async () => {
			return getFactoryInfo(publicClient, chainConfig.factory)
		},
		staleTime: 60_000,
	})

	// User's USDC balance query
	const { data: usdcBalance, refetch: refetchBalance } = useQuery({
		queryKey: ["usdc-balance", address, factoryInfo?.usdc],
		queryFn: async () => {
			if (!address || !factoryInfo?.usdc) {
				return null
			}
			const balance = await publicClient.readContract({
				address: factoryInfo.usdc,
				abi: erc20Abi,
				functionName: "balanceOf",
				args: [address],
			})
			return balance
		},
		enabled: !!address && !!factoryInfo?.usdc,
		staleTime: 30_000,
		refetchOnWindowFocus: true,
	})

	// Parse form values with validation
	const weekId = formState.weekId
	const binaryReserveUsd = Math.max(0, Number(formState.binaryReserveUsd) || 0)
	const featuredReserveUsd = Math.max(0, Number(formState.featuredReserveUsd) || 0)
	const launchFeeBps = Math.max(0, Math.min(10000, Number(formState.feeBps) || 0))
	const launchOpenTime = toEpochSeconds(formState.openTimeSec)
	const launchCutoffTime = toEpochSeconds(formState.cutoffTimeSec)

	// Calculate liquidity parameters from reserve amounts
	// For uniform markets: reserve = b × ln(n), so b = reserve / ln(n)
	// Ensure minimum of 1 to avoid division issues
	const binaryLiquidityParam = binaryReserveUsd > 0 ? Math.floor(binaryReserveUsd / Math.log(2)) : 0
	const featuredLiquidityParam =
		featuredReserveUsd > 0 ? Math.floor(featuredReserveUsd / Math.log(2)) : 0

	// Reserve previews - accounts for custom odds (seeded markets need more reserve)
	const binaryReservePreview = useMemo(() => {
		const liquidityParam = BigInt(binaryLiquidityParam)
		const marketCount = entries.length
		const uniformReserve = calculateReserve(liquidityParam, 2)

		// For oddsToQuantities, we need to pass reserve (not b)
		// reserve = b * ln(n) for uniform markets
		const reserveForQuantities = BigInt(Math.round(binaryLiquidityParam * Math.log(2)))

		// Calculate total by summing individual market reserves
		let total = 0n
		let seededCount = 0
		let extraReserve = 0n
		for (let i = 0; i < marketCount; i++) {
			const customOdds = formState.binaryOdds.get(i)
			if (customOdds && customOdds.length === 2) {
				// Validate odds before calculating - skip if invalid (use uniform instead)
				const validation = validateOdds(customOdds)
				if (validation.valid && reserveForQuantities > 0n) {
					// Seeded market - calculate with quantities
					// oddsToQuantities expects reserve in dollars, calculateSeededReserve expects b in dollars
					const quantities = oddsToQuantities(customOdds, reserveForQuantities)
					const seededReserve = calculateSeededReserve(liquidityParam, quantities)
					total += seededReserve
					extraReserve += seededReserve - uniformReserve
					seededCount++
				} else {
					// Invalid odds - treat as uniform for preview
					total += uniformReserve
				}
			} else {
				// Uniform market
				total += uniformReserve
			}
		}

		return { perMarket: uniformReserve, total, seededCount, extraReserve }
	}, [binaryLiquidityParam, entries.length, formState.binaryOdds])

	const featuredReservePreview = useMemo(() => {
		const liquidityParam = BigInt(featuredLiquidityParam)
		const uniformReserve = calculateReserve(liquidityParam, 2)

		// For oddsToQuantities, we need to pass reserve (not b)
		const reserveForQuantities = BigInt(Math.round(featuredLiquidityParam * Math.log(2)))

		if (formState.featuredOdds && formState.featuredOdds.length === 2) {
			// Validate odds before calculating - return uniform if invalid
			const validation = validateOdds(formState.featuredOdds)
			if (validation.valid && reserveForQuantities > 0n) {
				// Seeded market - calculate with quantities
				const quantities = oddsToQuantities(formState.featuredOdds, reserveForQuantities)
				const seededReserve = calculateSeededReserve(liquidityParam, quantities)
				return {
					reserve: seededReserve,
					extraReserve: seededReserve - uniformReserve,
					isSeeded: true,
				}
			}
			// Invalid odds - show as seeded but use uniform reserve for preview
			return { reserve: uniformReserve, extraReserve: 0n, isSeeded: true }
		}
		// Uniform market
		return { reserve: uniformReserve, extraReserve: 0n, isSeeded: false }
	}, [featuredLiquidityParam, formState.featuredOdds])

	const totalReservePreview = useMemo(() => {
		return binaryReservePreview.total + featuredReservePreview.reserve
	}, [binaryReservePreview.total, featuredReservePreview.reserve])

	// Calculate expected slippage for sample trade sizes
	// Slippage formula for LMSR: for a buy at price p, buying amount a,
	// price impact ≈ a / (2 × b × p × (1-p)) for small trades
	const slippagePreview = useMemo(() => {
		const sampleTrades = [10, 50, 100, 200] // Sample trade sizes in USD

		// Binary market at 50% odds
		const binarySlippage = sampleTrades.map(trade => {
			if (binaryLiquidityParam <= 0) {
				return 0
			}
			// Approximate slippage: shares ≈ trade / price, price moves by shares / b
			// For 50% odds, buying $X gets ~2X shares, moving price by ~X/b
			const shares = trade * 2 // At 50%, $1 = ~2 shares
			const priceMove = shares / binaryLiquidityParam
			return priceMove * 100 // Convert to percentage
		})

		// Custom question market at 50% odds (2 outcomes)
		const featuredSlippage = sampleTrades.map(trade => {
			if (featuredLiquidityParam <= 0) {
				return 0
			}
			// At 50% odds, $1 = ~2 shares
			const shares = trade * 2
			const priceMove = shares / featuredLiquidityParam
			return priceMove * 100
		})

		return { sampleTrades, binarySlippage, featuredSlippage }
	}, [binaryLiquidityParam, featuredLiquidityParam])

	// Launch function
	const launch = useCallback(async (): Promise<LaunchResult> => {
		// Debug logging for E2E tests
		const debug = (msg: string, ...args: unknown[]) => {
			if (E2E_MODE) {
				// biome-ignore lint/suspicious/noConsole: E2E debug logging
				console.log(`[Launch Debug] ${msg}`, ...args)
			}
		}

		debug("Starting launch...", {
			hasWalletClient: !!walletClient,
			address,
			factoryUsdc: factoryInfo?.usdc,
			entriesCount: entries?.length,
			snapshotDetails: snapshotDetails
				? { id: snapshotDetails.id, ipfsCid: snapshotDetails.ipfsCid }
				: null,
			adminPassword: adminPassword ? "[SET]" : "[NOT SET]",
		})

		if (!walletClient || !address) {
			debug("FAILED: No wallet connected")
			return { success: false, error: "Connect a wallet to create markets." }
		}
		if (!factoryInfo?.usdc) {
			debug("FAILED: Factory USDC not loaded")
			return { success: false, error: "Factory USDC address not loaded yet." }
		}
		if (!entries || entries.length < 1) {
			debug("FAILED: Not enough entries", entries?.length)
			return { success: false, error: "Need at least 1 whitelisted creator in leaderboard data." }
		}
		if (!snapshotDetails) {
			debug("FAILED: No snapshot selected")
			return { success: false, error: "Please select a snapshot for market creation." }
		}

		const timeValidation = validateMarketTimes(launchOpenTime, launchCutoffTime)
		if (!timeValidation.valid) {
			debug("FAILED: Time validation", timeValidation.error)
			return { success: false, error: timeValidation.error }
		}

		// Validate custom odds before launching
		for (const [index, odds] of formState.binaryOdds.entries()) {
			const validation = validateOdds(odds)
			if (!validation.valid) {
				debug("FAILED: Binary market odds validation", { index, odds, error: validation.error })
				return {
					success: false,
					error: `Binary market #${index + 1} has invalid odds: ${validation.error}`,
				}
			}
		}

		if (formState.featuredOdds) {
			const featuredValidation = validateOdds(formState.featuredOdds)
			if (!featuredValidation.valid) {
				debug("FAILED: Featured market odds validation", {
					odds: formState.featuredOdds,
					error: featuredValidation.error,
				})
				return {
					success: false,
					error: `Custom question has invalid odds: ${featuredValidation.error}`,
				}
			}
		}

		debug("Validation passed, starting launch process...")
		setIsLaunching(true)

		try {
			// Upload to IPFS if not already uploaded
			let ipfsCid = snapshotDetails.ipfsCid
			debug("IPFS CID status:", ipfsCid ? "Already uploaded" : "Needs upload")

			if (!ipfsCid) {
				if (!adminPassword) {
					debug("FAILED: No admin password for IPFS upload")
					return { success: false, error: "Admin authentication required" }
				}
				debug("Starting IPFS upload...")
				setLaunchProgress("Uploading snapshot to IPFS...")
				const result = await uploadSnapshotToIpfsAction({
					snapshotId: snapshotDetails.id,
					adminPassword,
				})
				debug("IPFS upload result:", result)
				if ("error" in result) {
					return { success: false, error: `IPFS upload failed: ${result.error}` }
				}
				ipfsCid = result.cid
			}

			setLaunchProgress("Calculating reserves...")

			const topEntries = entries // Already filtered by whitelist
			const feeBps = BigInt(launchFeeBps)
			const openTime = BigInt(launchOpenTime)
			const cutoffTime = BigInt(launchCutoffTime)

			// Build market batches
			const binaryLiqParam = BigInt(binaryLiquidityParam)
			const featuredLiqParam = BigInt(featuredLiquidityParam)

			// Build entity keys and salts for binary markets
			const binaryEntityKeys: Hex[] = []
			const binarySalts: Hex[] = []
			const binaryLiqParams: bigint[] = []
			const binaryCreators: Address[] = []
			const binaryMarketIds: string[] = []

			for (const entry of topEntries) {
				const entityKey = keccak256(stringToBytes(entry.handle || entry.displayName))
				const marketId = createId()
				const salt = keccak256(stringToBytes(marketId))
				binaryEntityKeys.push(entityKey)
				binarySalts.push(salt)
				binaryLiqParams.push(binaryLiqParam)
				binaryCreators.push(address)
				binaryMarketIds.push(marketId)
			}

			// Custom question entity key and salt
			const customQuestionEntityKey = keccak256(
				stringToBytes(`${CUSTOM_QUESTION_CONFIG.handlePrefix}-week-${weekId}`),
			)
			const customQuestionMarketId = createId()
			const customQuestionSalt = keccak256(stringToBytes(customQuestionMarketId))

			// Convert custom odds to initialOdds arrays
			const binaryInitialOdds: (number[] | undefined)[] = []
			for (let i = 0; i < topEntries.length; i++) {
				binaryInitialOdds.push(formState.binaryOdds.get(i))
			}

			// Build batches
			const batches: MarketBatchConfig[] = [
				{
					salts: binarySalts,
					entityKeys: binaryEntityKeys,
					liquidityParams: binaryLiqParams,
					creators: binaryCreators,
					bucketCount: 2, // Binary markets
					initialOdds: binaryInitialOdds,
				},
				{
					salts: [customQuestionSalt],
					entityKeys: [customQuestionEntityKey],
					liquidityParams: [featuredLiqParam],
					creators: [address],
					bucketCount: 2, // Custom question (binary)
					initialOdds: formState.featuredOdds ? [formState.featuredOdds] : undefined,
				},
			]

			// Calculate total reserve
			const totalReserve = calculateTotalReserve(batches)
			const binaryReservePerMarket = calculateReserve(binaryLiqParam, 2)
			const featuredReserve = calculateReserve(featuredLiqParam, 2)
			debug("Reserves calculated:", {
				total: totalReserve.toString(),
				binaryPerMarket: binaryReservePerMarket.toString(),
				featured: featuredReserve.toString(),
			})

			// Sign permit
			setLaunchProgress("Preparing permit signature...")
			const deadline = BigInt(deadlineSec(3600)) // 1 hour deadline

			debug("Fetching token name and nonce...")
			const [tokenName, permitNonce] = await Promise.all([
				getTokenName(publicClient, factoryInfo.usdc),
				getPermitNonce(publicClient, factoryInfo.usdc, address),
			])
			debug("Token info:", { tokenName, permitNonce: permitNonce.toString() })

			setLaunchProgress("Please sign the USDC permit...")
			debug("Requesting permit signature...")
			const permitSig = await signPermit({
				walletClient,
				tokenAddress: factoryInfo.usdc,
				tokenName,
				tokenVersion: "2", // USDC on Base uses version 2
				permit: {
					owner: address,
					spender: chainConfig.factory,
					value: totalReserve,
					nonce: permitNonce,
					deadline,
				},
			})
			debug("Permit signed:", { v: permitSig.v, r: `${permitSig.r.slice(0, 20)}...` })

			// Create all markets in one transaction
			setLaunchProgress(`Creating ${topEntries.length} binary markets + custom question...`)
			debug("Creating markets...")

			const txHash = await createAllMarketsWithPermit(walletClient, {
				factoryAddress: chainConfig.factory,
				implementation: chainConfig.implementation,
				usdcAddress: factoryInfo.usdc,
				publicClient,
				feeBps,
				openTime,
				cutoffTime,
				batches,
				permitSig,
				permitDeadline: deadline,
				permitAmount: totalReserve,
			})

			setLaunchProgress("Waiting for transaction confirmation...")
			const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash })

			if (receipt.status === "reverted") {
				return { success: false, error: "Market creation transaction reverted" }
			}

			// Extract market addresses from logs
			const marketAddresses: Address[] = []
			for (const log of receipt.logs) {
				if (log.address.toLowerCase() !== chainConfig.factory.toLowerCase()) {
					continue
				}
				try {
					const decoded = decodeEventLog({
						abi: breakoutMarketFactoryAbi,
						data: log.data,
						topics: log.topics,
					})
					if (decoded.eventName === "MarketCreated") {
						marketAddresses.push(decoded.args.market)
					}
				} catch {
					// Ignore non-matching logs
				}
			}

			if (marketAddresses.length === 0) {
				return { success: false, error: "No markets were created - check transaction" }
			}

			// Record markets in Convex
			const binaryMarketAddresses = marketAddresses.slice(0, topEntries.length)
			const featuredMarketAddress = marketAddresses[topEntries.length]

			setLaunchProgress(`Recording ${marketAddresses.length} markets in database...`)

			// Ensure admin password is set for database operations
			if (!adminPassword) {
				return { success: false, error: "Admin authentication required to record markets" }
			}

			// Build markets array for batch upsert
			const marketsToUpsert: Array<{
				salt: string
				entityKey: string
				marketAddress: string
				liquidityParam: number
				reserveUsdc: string
				displayName?: string
				handle?: string
				avatarUrl?: string
				marketType: "binary" | "fall_from_grace"
				outcomeLabels?: string[]
				startRank?: number
				mindsharePct?: number
				customTitle?: string
				customDescription?: string
			}> = []

			// Binary markets
			for (let i = 0; i < topEntries.length; i++) {
				const entry = topEntries[i]
				const marketAddress = binaryMarketAddresses[i]
				const salt = binarySalts[i]
				const entityKey = binaryEntityKeys[i]

				if (!entry || !marketAddress || !salt || !entityKey) {
					continue
				}

				const labels = BINARY_OUTCOME_LABELS

				marketsToUpsert.push({
					salt,
					entityKey,
					marketAddress,
					liquidityParam: binaryLiquidityParam,
					reserveUsdc: binaryReservePerMarket.toString(),
					displayName: entry.displayName,
					handle: entry.handle,
					avatarUrl: entry.avatarUrl,
					marketType: "binary",
					outcomeLabels: [...labels],
					startRank: entry.rank,
					mindsharePct: entry.mindsharePct,
				})
			}

			// Custom question market
			if (
				featuredMarketAddress &&
				featuredMarketAddress !== "0x0000000000000000000000000000000000000000"
			) {
				marketsToUpsert.push({
					salt: customQuestionSalt,
					entityKey: customQuestionEntityKey,
					marketAddress: featuredMarketAddress,
					liquidityParam: featuredLiquidityParam,
					reserveUsdc: featuredReserve.toString(),
					displayName: CUSTOM_QUESTION_CONFIG.displayName,
					handle: `${CUSTOM_QUESTION_CONFIG.handlePrefix}-week-${weekId}`,
					marketType: "binary",
					outcomeLabels: [...CUSTOM_QUESTION_CONFIG.outcomeLabels],
					avatarUrl: CUSTOM_QUESTION_CONFIG.avatarUrl,
					customTitle: CUSTOM_QUESTION_CONFIG.question,
					customDescription: CUSTOM_QUESTION_CONFIG.customDescription,
				})
			}

			// Single batch call instead of 20+ individual calls
			await upsertMarketsBatchAction({
				adminPassword,
				weekId,
				openTimeSec: launchOpenTime,
				cutoffTimeSec: launchCutoffTime,
				feeBps: launchFeeBps,
				txHash,
				startSnapshotId: snapshotDetails.id,
				markets: marketsToUpsert,
			})

			return {
				success: true,
				txHash,
				marketCount: marketAddresses.length,
			}
		} catch (error) {
			if (E2E_MODE) {
				console.error("[Launch Debug] ERROR:", error)
			}
			return {
				success: false,
				error: error instanceof Error ? error.message : "Failed to launch markets",
			}
		} finally {
			setIsLaunching(false)
			setLaunchProgress(null)
		}
	}, [
		walletClient,
		address,
		factoryInfo,
		entries,
		snapshotDetails,
		launchOpenTime,
		launchCutoffTime,
		weekId,
		binaryLiquidityParam,
		featuredLiquidityParam,
		launchFeeBps,
		chainConfig,
		publicClient,
		adminPassword,
		uploadSnapshotToIpfsAction,
		upsertMarketsBatchAction,
		formState.binaryOdds, // Map identity - stable unless replaced
		formState.featuredOdds,
	])

	// Balance sufficiency check
	const balanceInfo = useMemo(() => {
		if (usdcBalance === null || usdcBalance === undefined) {
			return { isLoading: true, hasSufficientBalance: false, balance: 0n, shortfall: 0n }
		}
		const hasSufficientBalance = usdcBalance >= totalReservePreview
		const shortfall = hasSufficientBalance ? 0n : totalReservePreview - usdcBalance
		return {
			isLoading: false,
			hasSufficientBalance,
			balance: usdcBalance,
			shortfall,
		}
	}, [usdcBalance, totalReservePreview])

	// Odds validation for UI feedback
	const oddsValidation = useMemo(() => {
		const errors: string[] = []

		// Validate binary market odds
		for (const [index, odds] of formState.binaryOdds.entries()) {
			const validation = validateOdds(odds)
			if (!validation.valid) {
				errors.push(`Binary market #${index + 1}: ${validation.error}`)
			}
		}

		// Validate featured odds
		if (formState.featuredOdds) {
			const validation = validateOdds(formState.featuredOdds)
			if (!validation.valid) {
				errors.push(`Custom question: ${validation.error}`)
			}
		}

		return {
			valid: errors.length === 0,
			errors,
		}
	}, [formState.binaryOdds, formState.featuredOdds])

	return {
		launch,
		isLaunching,
		launchProgress,
		factoryInfo,
		reserves: {
			binaryPerMarket: binaryReservePreview.perMarket,
			binaryTotal: binaryReservePreview.total,
			binarySeededCount: binaryReservePreview.seededCount,
			binaryExtraReserve: binaryReservePreview.extraReserve,
			featured: featuredReservePreview.reserve,
			featuredExtraReserve: featuredReservePreview.extraReserve,
			featuredIsSeeded: featuredReservePreview.isSeeded,
			total: totalReservePreview,
		},
		liquidityParams: {
			binary: binaryLiquidityParam,
			featured: featuredLiquidityParam,
		},
		slippagePreview,
		balanceInfo,
		refetchBalance,
		oddsValidation,
	}
}
